import { Injectable } from '@angular/core';
import { JeopardyGame, JeopardyGameEngine, JeopardyRound, JeopardyUser } from '@app/shared/graphql/types';
import { CategoryViewModel } from '@app/shared/rest-api';
import { combineLatest, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { CustomTopic, DifficultyLevel, GameService } from './graphql/game.service';
import { SessionService } from './session.service';
import { UmbracoService } from './umbraco.service';

@Injectable({
  providedIn: 'root',
})
export class GameEngineService {
  private _gameId: number;
  private _active: boolean;
  private _started: boolean;
  private _currentRound: JeopardyRound;
  private _players: JeopardyUser[];
  private _connectedPlayer: JeopardyUser;
  private _topics: CustomTopic[];
  private _selectedTopic: CustomTopic;
  private _gameEngine: JeopardyGameEngine;
  private _lastTopic: CustomTopic;
  private _roundStarted: boolean;
  private _difficulty: DifficultyLevel;
  private _5050Jokers: number;
  private _skipJokers: number;
  private _isMultiplayer: boolean;
  private _isAdvancedMode: boolean;
  private _isPrivateRoom: boolean;
  private _roomName: string;
  private _rounds: JeopardyRound[];
  private _noOfPlayers: number;
  private _createdOn: string;

  constructor(
    private gameService: GameService,
    private umbracoService: UmbracoService,
    private sessionService: SessionService,
  ) {
    this._topics = null;
    this.clearGame();
  }

  public get gameId() {
    return this._gameId;
  }

  public get active() {
    return this._active;
  }

  public set active(value: boolean) {
    this._active = value;
  }

  public get started() {
    return this._started;
  }
  public set selectedTopic(topic: CustomTopic) {
    this._selectedTopic = topic;
  }
  public get selectedTopic() {
    return this._selectedTopic;
  }

  public set started(value: boolean) {
    this._started = value;
  }

  public getCurrentRound() {
    return this.gameService.getGameRound(this._gameId).pipe(
      tap(round => {
        if (!round) {
          this._currentRound = this._rounds[0];
          this._roundStarted = !!this._rounds[0].timeStart;
        } else {
          this._currentRound = round;
          this._roundStarted = !!round.timeStart;
        }
      }),
      catchError(err => {
        throw err
      })
    );
  }

  public get currentRound() {
    return this._currentRound;
  }

  // public set currentRound(value: Round) {
  //     this._currentRound = value;
  // }

  public get players() {
    return this._players;
  }

  public loadPlayers(): Observable<JeopardyUser[]> {
    return this.gameService.getUserByGameId(this._gameId).pipe(
      tap(players => {
        this._players = players;
        this._connectedPlayer = this.getConnectedPlayerDetails();
        // this.jokersSkip = this.game;
        // this.jokers5050 = ;
      }),
      catchError(err => {
        throw err;
      })
    );
  }

  public get connectedPlayer() {
    return this._connectedPlayer;
  }

  // public set connectedPlayer(value: GameUser) {
  //     this._connectedPlayer = value;
  // }

  public loadTopics(): Observable<CustomTopic[]> {
    return this.umbracoService.topics$.pipe(
      map(topics => topics.filter(topic => topic.title).map((topic: CategoryViewModel, index) => this.toCustomTopic(topic, index))),
      tap(topics => {
        this._topics = topics;
      }),
      catchError(err => {
        throw err
      })
    );
  }

  public get topics() {
    return this._topics;
  }

  public findTopic(topicId: string) {
    return this._topics.find(topic => topic.id === topicId);
  }

  public get gameEngineDetails() {
    return this._gameEngine;
  }

  public get difficulty() {
    return this._difficulty;
  }

  public set difficulty(value: DifficultyLevel) {
    this._difficulty = value;
  }

  public get roundStarted() {
    return this._roundStarted;
  }
  public get createdOn() {
    return this._createdOn;
  }

  public set roundStarted(value: boolean) {
    this._roundStarted = value;
  }

  public get lastTopic(): CustomTopic {
    return this._lastTopic;
  }

  public set lastTopic(value: CustomTopic) {
    this._lastTopic = value;
  }

  public get jokers5050(): number {
    return this._5050Jokers;
  }

  public set jokers5050(value: number) {
    this._5050Jokers = value;
  }

  public get jokersSkip(): number {
    return this._skipJokers;
  }

  public set jokersSkip(value: number) {
    this._skipJokers = value;
  }

  public get isMultiplayer(): boolean {
    return this._isMultiplayer;
  }

  public set isMultiplayer(value: boolean) {
    this._isMultiplayer = value;
  }

  public get isAdvancedMode(): boolean {
    return this._isAdvancedMode;
  }

  public set isAdvancedMode(value: boolean) {
    this._isAdvancedMode = value;
  }

  public set isPrivateRoom(value: boolean) {
    this._isPrivateRoom = value;
  }

  public get isPrivateRoom(): boolean {
    return this._isPrivateRoom;
  }
  public get roomName(): string {
    return this._roomName;
  }

  public set roomName(value: string) {
    this._roomName = value;
  }

  public loadGameRounds(): Observable<JeopardyRound[]> {
    return this.gameService.getGameRounds(this._gameId).pipe(
      tap(rounds => {
        if (rounds) {
          this._rounds = rounds;
        }
      }),
      catchError(err => {
        throw err;
      })
    );
  }

  public get gameRounds() {
    return this._rounds;
  }

  public get noOfPlayers(): number {
    return this._noOfPlayers;
  }

  public init(gameId: number): Observable<JeopardyGame> {
    if (this._gameId || !gameId) {
      console.log('whyyyyyyyyyyyy', this._gameId);
      return;
    }

    this._gameId = gameId;

    return combineLatest([
      this.loadGameRounds(),
      this.getCurrentRound(),
      this.loadPlayers(),
      this.loadTopics(),
      this.gameService.getGameDetails(gameId)
    ]).pipe(
      map(data => data[4]),
      tap((game: JeopardyGame) => {
        this._active = game.active;
        this._isAdvancedMode = game.advancedMode;
        //TODO: this._isMultiplayer = game.multiplayer;
        this._isPrivateRoom = game.private;
        this._roomName = game.roomName;
        this._gameEngine = game.gameEngine;
        this._noOfPlayers = game.players;
        this._createdOn = game.createdOn;
      })
    );
  }

  public reloadGameData() {
    return combineLatest([
      this.loadGameRounds(),
      this.getCurrentRound(),
      this.loadPlayers()
    ]);
  }

  public clearGame() {
    this._gameId = null;
    this._active = false;
    this._started = false;
    this._currentRound = null;
    this._players = null;
    this._connectedPlayer = null;
    this._gameEngine = null;
    this._lastTopic = null;
    this._roundStarted = false;
    this._difficulty = null;
    this._5050Jokers = null;
    this._skipJokers = null;
    this._isMultiplayer = false;
    this._isPrivateRoom = false;
    this._roomName = null;
    this._rounds = null;
    this._noOfPlayers = null;

    console.log(this._gameId);
  }

  private getConnectedPlayerDetails(): JeopardyUser {
    if (this._players?.length === 1) {
      return this._players[0];
    }

    const currentUser = this.sessionService.getUser();
    const deviceDetails = this.gameService.getDeviceDetails();

    const filtered = this._players?.filter(user =>
      user.language === currentUser.location.language.isoCode2Letters &&
      user.location === currentUser.location.country.countryId &&
      user.name === currentUser.username &&
      user.deviceDetected === deviceDetails[0] &&
      user.deviceIdentifier === deviceDetails[1]
    );

    if (filtered?.length > 0) {
      return filtered[0];
    }
    return null;
  }

  private toCustomTopic(topic: CategoryViewModel, index: number): CustomTopic {
    const start = index === 0 ? 0 : (index * 30) + 1;
    const end = (index + 1) * 30;

    let customTopic = {
      id: topic.key,
      name: topic.title,
      start,
      end,
      iconCls: '',
      icon: topic.iconUrl,
      defaultPictureUrl: topic.defaultPictureUrl,
      iconDescription: '',
      defaultPictureUrlDescription: ''
    }

    return customTopic;
  }
}
